import { Link } from "@/lib/nav";
import { ReactNode } from "react";

import type { InviteView } from "@buildpy/core/views/w0b/invite";
import { logout } from "@/api/actions/org";
import { BrandLogo } from "@buildpy/ui/brand-logo";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { EMPTY_MARK } from "@buildpy/core/lib/format";
import { ROLE_LABEL } from "@buildpy/core/modules/org/types";
import { LOGIN_PATH } from "@buildpy/core/modules/onboarding/rules";
import InviteForm from "./invite-form";

// 초대 수락 — 토큰이 곧 범위 · 못 쓰는 링크는 404 가 아니라 다음 할 일(대표에게 요청)을 말하는 안내문.
const Page = ({ data }: { data: InviteView }) => {
  const { token, denial, invite, orgName, signedIn, signedInAs } = data;

  return (
    <Shell>
      <div className="flex flex-col items-start gap-y-2">
        <BrandLogo size="sm" />
        <h1 className="text-2xl font-semibold tracking-tight">초대</h1>
      </div>

      {denial || !invite ? (
        <>
          <p className="text-sm text-muted-foreground">{denial}</p>
          <Button asChild className="w-full" variant="outline">
            <Link href={LOGIN_PATH}>로그인 화면으로</Link>
          </Button>
        </>
      ) : (
        <>
          <dl className="flex flex-col gap-y-2 rounded-lg border border-border bg-muted/40 p-4 text-sm">
            <Row label="회사" value={orgName ?? EMPTY_MARK} />
            <Row label="권한" value={ROLE_LABEL[invite.role]} />
            <Row label="이메일" value={invite.email} />
          </dl>

          {signedIn ? (
            // 이미 다른 계정(멤버·조직 없는 셀프 계정 모두)으로 앉아 있다. 그대로 수락시키면 세션이 조용히 바뀐다.
            <div className="flex flex-col gap-y-3">
              <p className="text-sm text-muted-foreground">
                지금{" "}
                {signedInAs ? (
                  <>
                    <span className="font-medium text-foreground">{signedInAs}</span> 계정으로
                  </>
                ) : (
                  "다른 계정으로"
                )}{" "}
                로그인돼 있습니다. 로그아웃한 뒤 다시 이 링크를 여세요.
              </p>
              <form
                action={async () => {
                  await logout();
                }}>
                <Button className="w-full" type="submit" variant="outline">
                  로그아웃
                </Button>
              </form>
            </div>
          ) : (
            <InviteForm token={token} />
          )}
        </>
      )}
    </Shell>
  );
};

export default Page;

const Shell = ({ children }: { children: ReactNode }) => (
  <main className="flex min-h-screen items-center justify-center bg-background p-4">
    <SectionCard as="div" className="flex w-full max-w-sm flex-col gap-y-6" padding="lg">
      {children}
    </SectionCard>
  </main>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="text-right font-medium">{value}</dd>
  </div>
);
